/**
 * WHY THIS MODULE EXISTS
 *
 * Every request in this app authenticates with the `auth` cookie, and that cookie is a
 * Firebase SESSION cookie: the backend mints it from an ID token and it carries a lifetime
 * of its own, far longer than the token's hour. Nothing on this side can read it — it is
 * HttpOnly — so the page never knows whether it is still good. The backend is the
 * authority, and it says so with a code: a request answered `INVALID_TOKEN`,
 * `COOKIE_REQUIRED` or `SESSION_EXPIRED` is a cookie that has to be minted again, and
 * `GET /v2/auth/me` with a Bearer ID token is the only thing that mints one.
 *
 * So this module keeps nothing fresh and owns no timer. Its whole job is what happens
 * around that one endpoint: ONE mint at a time, so a wake-up burst of parallel refetches
 * cannot write four cookies over each other; ONE recovery per refusal, so a session that
 * is over does not become one `/auth/me` per request; a pause after a failure, so a
 * session that can never be renewed eventually stops asking; and one held moment while the
 * credential itself is being rewritten, when nothing else may go out at all.
 *
 * One shape to know at the call sites: a sign-out answers `recover()` two different ways.
 * It resolves `'stale-session'` when the logout was already under way as the call entered,
 * and rejects with `SessionMovedError` when the logout lands while the call is awaiting
 * Firebase or `/auth/me` — so a caller has to handle both, not just the resolved one.
 *
 * All state lives here, injected and clock-free, because `api/axios.ts` cannot be unit
 * tested in this package's jest runner.
 */

/** The claims that identify ONE sign-in of one account. See `decodeJwtLoginKey`. */
export type LoginKey = {
  authTime: number;
  identityTenantId: string;
  subject: string;
};

/** What a request carries so a late failure can be judged against what is true by then. */
export type SessionCookieStamp = {
  contextKey: string;
  epoch: number;
  generation: number;
};

export type SessionCookieFailureSource = 'recover' | 'renew' | 'replay';

/**
 * What a recovery attempt means for the request that triggered it.
 *
 * `recover()` cannot return void: the Axios layer has to tell "the cookie is fresh now,
 * replay" apart from "this request belongs to an auth session or tenant that no longer
 * exists, do NOT replay it under the current one". A void result made every outcome look
 * like success, and a replay under the wrong session is exactly what that hides.
 */
export type SessionCookieRecovery =
  | 'already-current'
  | 'renewed'
  | 'stale-session';

export type SessionCookieDeps<T> = {
  /**
   * The claims that name ONE sign-in — `sub`, the identity tenant and `auth_time` — or
   * null when the token cannot be read. Local and untrusted: it distinguishes a rollover
   * from a new login, and is never an authorization proof.
   */
  decodeLoginKey: (token: string) => LoginKey | null;
  /** the acting tenant, from the app's single `resolveActingTenantId` chokepoint */
  getContextKey: () => string;
  /** Firebase `currentUser.getIdToken(forceRefresh)`; undefined when signed out */
  getIdToken: (forceRefresh: boolean) => Promise<string | undefined>;
  /**
   * Is this failure the connection being unreachable rather than the session being over?
   *
   * The guard cannot tell: it never sees a status, and the one read it does own —
   * `getIdToken` — talks to Google, not to us. Injected so the module stays clock-free and
   * SDK-free; absent, nothing is a connectivity fault and every failure is news.
   */
  isConnectivityFault?: (error: unknown) => boolean;
  /** injected clock, in MILLISECONDS */
  now: () => number;
  /** observability only — a hard reject is otherwise completely silent */
  reportHardFailure: (
    source: SessionCookieFailureSource,
    error: unknown,
  ) => void;
  /** `GET /v2/auth/me` with `token` as Bearer — the only thing that mints the cookie */
  renewCookie: (token: string, contextKey: string) => Promise<T>;
};

export type RenewOptions = {
  /** only a `/auth/me` issued after this call may answer it */
  afterMutation?: boolean;
};

/** The handle that ends a credential transition. See `beginCredentialTransition`. */
export type CredentialTransition = {
  /**
   * Release everything parked on the hold. Idempotent, and safe to call from a `finally`:
   * a handle whose transition is already over — ended by a logout, or by an earlier call —
   * never touches the one running now.
   */
  end: () => void;
};

export type JoinOptions = {
  /**
   * This caller IS the credential transition — the PATCH that rewrites the password, and
   * the one recovery it may need to get that request out. Everyone else waits for `end()`.
   */
  transitionOwner?: boolean;
};

export type SessionCookieGuard<T> = {
  /**
   * The account's own credential is about to be rewritten — the corp password change — and
   * the backend revokes its Firebase refresh tokens before it answers the write. Between
   * that answer and the new sign-in every forced refresh is refused, and the Firebase SDK
   * answers a refused refresh by signing the user out locally. So nothing may go looking
   * for a new token in that window.
   *
   * Reserves the hold at once — a second transition waits for the first to end rather than
   * overwriting it — then drains the mint already in flight and only then resolves. While
   * it is held, `join()` and `recover()` wait; the caller performing the transition passes
   * with `{ transitionOwner: true }`, because it has to reach the very request that ends
   * the hold. `renew()` is never held: it is how the transition confirms its new login.
   *
   * This is NOT `quiesce()`. That one is the logout latch and refuses to mint at all,
   * which would block the mint this hold exists to wait for.
   */
  beginCredentialTransition: () => Promise<CredentialTransition>;
  /** The current stamp, synchronously and without reading Firebase. */
  getStamp: () => SessionCookieStamp;
  /** Does this stamp still describe the session and context in force right now? */
  isCurrent: (
    stamp: SessionCookieStamp | undefined,
    includeGeneration?: boolean,
  ) => boolean;
  /** Is this token the very login a mint already confirmed, in this acting context? */
  isSameLogin: (token: string) => boolean;
  /**
   * The request barrier. Waits for a mint already in flight and for a credential
   * transition, and nothing else: it never reads Firebase, never starts a mint and never
   * consults the cooldown.
   */
  join: (options?: JoinOptions) => Promise<SessionCookieStamp>;
  /**
   * A mint was just confirmed for the epoch and context in force — the cookie in the
   * browser is new and the generation has moved. Returns the unsubscribe.
   *
   * For the one consumer that cannot wait for a request of its own to fail: the socket
   * (auth-11 P2-3). Its handshake is refused for a dead cookie, and nothing in the effect's
   * dependency list moves when the HTTP layer later repairs the session — so without this
   * notice a socket whose recovery the cooldown declined stays offline until a reload.
   *
   * Fires on the confirmed path only, synchronously, inside the mint. A mint that failed, a
   * recovery that never minted, and a write that landed after a sign-out or a tenant switch
   * — none of them confirmed anything, and none of them fire. A subscriber that throws is
   * swallowed: it is an observer, and the cookie is already written.
   *
   * Subscriptions outlive `reset()` and `quiesce()`. The lifetime belongs to the
   * subscriber — the socket effect keeps one for as long as its instance lives, across
   * every sign-out the guard sees in between — and it ends by calling the unsubscribe.
   */
  onConfirmed: (listener: () => void) => () => void;
  /** Answer to a 401/403: force a new identity and a new cookie, once. */
  recover: (
    rejected: SessionCookieStamp | undefined,
    options?: JoinOptions,
  ) => Promise<SessionCookieRecovery>;
  /**
   * The canonical app-owned `/auth/me`. Shares the single flight with everything else.
   *
   * `afterMutation` is for the caller that just CHANGED the account — accepted an
   * invitation, a privacy policy, a preferred organization — and whose answer becomes
   * `currentUser`. Such a caller may not be answered by a request that was already on the
   * wire when it asked. Every other explicit renewal (the `onIdTokenChanged` listener's,
   * fired by the guard's own forced refresh) joins whatever is in flight: refusing there
   * cost every rollover and every recovery a second `/auth/me`, on every tab.
   */
  renew: (token: string, options?: RenewOptions) => Promise<T>;
  /**
   * Sign-out is starting. Drain the renewal already in flight and refuse to start another
   * until `reset()`.
   *
   * `POST /auth/logout` is what deletes the server-side cookie, and it bypasses the barrier
   * — so on its own it races an `/auth/me` that is still in flight and loses to it: the
   * browser accepts that response's `Set-Cookie` after the logout has already cleared one.
   * Bumping the epoch cannot undo that; it is a statement about what THIS tab may trust,
   * not about what the browser is holding. And the latch outlives the drain because
   * Firebase's `signOut()` is awaited afterwards: until it resolves the identity is still
   * there, and anything that asks the guard in that window would mint a fresh cookie —
   * confirmed, since the epoch has moved for everyone equally.
   */
  quiesce: () => Promise<void>;
  /**
   * The replay that followed a recovery was refused with the same answer. The renewal
   * "succeeded" and the session is still dead — the one failure `recover()` cannot see.
   */
  replayRefused: (
    stamp: SessionCookieStamp | undefined,
    error: unknown,
  ) => void;
  /** Auth session ended. Nothing confirmed before this may be trusted or replayed. */
  reset: () => void;
};

/**
 * After a failed renewal, wait this long before attempting another. Without it a session
 * that can never be renewed — a revoked refresh token — becomes one `/auth/me` per
 * request, on every reconnect wave, forever.
 */
export const RENEWAL_FAILURE_COOLDOWN_MS = 30_000;

/**
 * Fail-closed stand-in for a caller whose `sub` cannot be read — an unreadable token, or a
 * recovery running before anything was ever confirmed. Such a caller has no identity of
 * its own to be scoped by, so it is covered by whatever cooldown entry is current: that
 * suppresses retries rather than letting them through.
 */
const UNKNOWN_SUBJECT = 'mfw:unknown-subject';

const noIdentityError = () =>
  new Error('No Firebase identity available to renew the session cookie');

/**
 * The one rejection that is not news: a sign-out or a tenant switch landed while `/auth/me`
 * was in flight, so the answer describes a session the app has already left. It happens on
 * every logout by design, and callers have to be able to tell it apart from a renewal that
 * actually failed — matching on the message string is not a contract anyone can keep.
 */
export class SessionMovedError extends Error {
  constructor() {
    super(
      'Session or tenant context changed while the cookie was being renewed',
    );
    this.name = 'SessionMovedError';
  }
}

const sessionMovedError = () => new SessionMovedError();

/** A promise and its resolver: one side waits, the other says when the waiting is over. */
const latch = () => {
  let open: () => void = () => undefined;
  const opened = new Promise<void>((resolve) => {
    open = resolve;
  });
  return { open, opened };
};

type Outcome<T> =
  | { error: unknown; kind: 'failed'; token: string | undefined }
  | { kind: 'skipped'; token: string | undefined }
  | { kind: 'wrote'; result: T; token: string };

type Operation<T> = {
  contextKey: string;
  epoch: number;
  promise: Promise<Outcome<T>>;
  /**
   * Which `/auth/me` this is, counted from the start of the module. It answers the one
   * question a claim comparison cannot: was this request already on the wire when the
   * caller asking about it entered? A caller that mutated something first can only be
   * answered by a request issued after it asked.
   */
  sequence: number;
};

type Cooldown = {
  atMs: number;
  bypassConsumed: boolean;
  contextKey: string;
  epoch: number;
  error: unknown;
  reported: boolean;
  subject: string;
};

export function createSessionCookieGuard<T>(
  deps: SessionCookieDeps<T>,
  cooldownMs = RENEWAL_FAILURE_COOLDOWN_MS,
): SessionCookieGuard<T> {
  let confirmedToken: null | string = null;
  /**
   * The login a mint actually confirmed, together with the acting context it was confirmed
   * in. `isSameLogin` is the whole reason it exists: it lets the `onIdTokenChanged`
   * listener tell an hourly rollover, which needs no mint, from a new sign-in, which does.
   */
  let confirmedLogin: null | { contextKey: string; key: LoginKey } = null;
  let confirmedSubject: null | string = null;
  let epoch = 0;
  let generation = 0;
  let inFlight: null | Operation<T> = null;
  /** Monotonic; the ordinal every operation is stamped with. */
  let sequence = 0;
  let cooldown: Cooldown | null = null;
  /**
   * Set by `quiesce()` for the length of a sign-out, cleared by `reset()`. While it is on,
   * no source may start a mint — not a recovery, not a caller that woke up at the barrier
   * and found the epoch had moved.
   */
  let closed = false;
  /**
   * The credential transition, while one is running. Its `done` is what `join` and
   * `recover` park on, and `end` is the only thing that releases them.
   */
  let transition: null | { done: Promise<void>; end: () => void } = null;
  /**
   * Callers parked on the mint in flight, each still to make the one decision the barrier
   * cannot see: adopt that outcome, or mint again behind it. They take it a microtask
   * AFTER the mint settles, so between those two moments `inFlight` is null and a follow-on
   * that is certain to start has not started yet. A `join()` that judged the outcome in
   * that gap would reject a request the follow-on is about to serve.
   */
  const undecided = new Set<Promise<void>>();
  /**
   * Who to tell when a mint is confirmed. Deliberately outside everything `reset()` clears:
   * this is not session state, it is the subscribers' own, and the socket effect that holds
   * one is still the same instance on the other side of a sign-out.
   */
  const confirmationListeners = new Set<() => void>();
  /**
   * Reporting is latched per UNHEALTHY STRETCH, not per cooldown entry. Entries expire and
   * the next failed attempt mints a fresh one, so "once per entry" was once per cooldown
   * for as long as an outage lasted — a toast and a Sentry event every 30 s on an active
   * screen. A successful write is the only thing that ends the stretch, and a reset starts
   * a new session, which is news of its own.
   */
  let failureReported = false;
  /**
   * When the current RUN of connectivity faults began, `null` while none is running. A
   * dropped connection is not the same news as a dead session, and on a waking machine it
   * is not news at all — but silence may not be permanent, so the run is timed.
   */
  let connectivityFaultSince: null | number = null;

  /**
   * Announce a confirmed mint. A copy of the set, because a subscriber unsubscribing from
   * inside its own callback — which is exactly what the socket's parked listener does — may
   * not change the round that is being delivered.
   */
  const announceConfirmed = () => {
    for (const listener of [...confirmationListeners]) {
      try {
        listener();
      } catch {
        // An observer's own failure is not the mint's. The cookie is written and the
        // generation has moved; rejecting here would hand every caller parked on this
        // operation a failure that never happened. Not `reportHardFailure` either — that
        // reporter describes a session that could not be renewed, and this one was.
      }
    }
  };

  const subjectOf = (token: string | undefined) =>
    (token ? deps.decodeLoginKey(token)?.subject : confirmedSubject) ??
    UNKNOWN_SUBJECT;

  /**
   * ONE sign-in of one account in one identity tenant. `sub` alone is not enough — a
   * re-login is the same `sub` — and `auth_time` alone is not enough either: UIDs are
   * identity-tenant-local, so two tenants can hand out the same one.
   */
  const sameKey = (a: LoginKey | null, b: LoginKey | null) =>
    a !== null &&
    b !== null &&
    a.subject === b.subject &&
    a.identityTenantId === b.identityTenantId &&
    a.authTime === b.authTime;

  const sameLoginToken = (a: string | undefined, b: string | undefined) =>
    a !== undefined &&
    b !== undefined &&
    sameKey(deps.decodeLoginKey(a), deps.decodeLoginKey(b));

  const stamp = (): SessionCookieStamp => ({
    contextKey: deps.getContextKey(),
    epoch,
    generation,
  });

  /**
   * Does the credential transition hold this caller? Only its owner passes — it has to be
   * able to reach the request that ends the hold — and `renew` is never asked at all.
   */
  const heldByTransition = (options: JoinOptions | undefined) =>
    transition !== null && options?.transitionOwner !== true;

  /** Wait the hold out. A second transition may start while the first is being released. */
  const awaitTransition = async (options: JoinOptions | undefined) => {
    for (;;) {
      const held = transition;
      if (held === null || options?.transitionOwner === true) return;
      await held.done;
    }
  };

  const report = (entry: Cooldown, source: SessionCookieFailureSource) => {
    if (entry.reported || failureReported) return;
    entry.reported = true;
    failureReported = true;
    try {
      deps.reportHardFailure(source, entry.error);
    } catch {
      // Observability must never replace the failure it was describing.
    }
  };

  /**
   * May this failure pass without a Sentry event and an on-screen notice?
   *
   * Only while the connection has been unreachable for less than one retry window. A
   * recovery's `getIdToken(true)` is a NETWORK call to Google, so a machine that wakes
   * without a connection fails it every time, and reporting that told the user their
   * session was over when it was not. One window later the verdict stands: a connection
   * that never comes back leaves the page refusing to fill in, and saying nothing about
   * that is the failure the notice exists to prevent.
   */
  const silentConnectivityFault = (error: unknown) => {
    if (!deps.isConnectivityFault?.(error)) {
      connectivityFaultSince = null;
      return false;
    }
    const at = deps.now();
    if (connectivityFaultSince === null) connectivityFaultSince = at;
    return at - connectivityFaultSince < cooldownMs;
  };

  const activeCooldown = (token: string | undefined) => {
    if (cooldown === null) return null;
    if (deps.now() - cooldown.atMs >= cooldownMs) {
      cooldown = null;
      return null;
    }
    const subject = subjectOf(token);
    const applies =
      cooldown.epoch === epoch &&
      cooldown.contextKey === deps.getContextKey() &&
      // The sentinel matches every entry on purpose: a caller that cannot name a subject
      // has no account of its own to be scoped by, and letting it through is precisely the
      // storm the entry exists to stop.
      (cooldown.subject === subject || subject === UNKNOWN_SUBJECT);
    return applies ? cooldown : null;
  };

  const rememberFailure = (outcome: {
    error: unknown;
    token: string | undefined;
  }) => {
    const existing = activeCooldown(outcome.token);
    if (existing) {
      // Keep the original `atMs`: a window that restarts on every observer never elapses.
      existing.error = outcome.error;
      return existing;
    }
    const entry: Cooldown = {
      atMs: deps.now(),
      bypassConsumed: false,
      contextKey: deps.getContextKey(),
      epoch,
      error: outcome.error,
      reported: false,
      subject: subjectOf(outcome.token),
    };
    cooldown = entry;
    return entry;
  };

  /**
   * The one place a failure turns into an answer. Each caller runs it for itself over the
   * SHARED outcome, so the cooldown entry is written once however many callers joined.
   */
  const applyPolicy = (
    source: SessionCookieFailureSource,
    outcome: Outcome<T>,
  ): Outcome<T> => {
    if (outcome.kind !== 'failed') return outcome;

    const entry = rememberFailure(outcome);
    if (!silentConnectivityFault(outcome.error)) report(entry, source);
    throw outcome.error;
  };

  const perform = async (
    explicitToken: string | undefined,
    contextKey: string,
    startEpoch: number,
  ): Promise<Outcome<T>> => {
    let token = explicitToken;
    try {
      if (token === undefined) {
        // A recovery has no token of its own: the cookie was refused, so the identity
        // behind it has to be re-read from Firebase, forced, exactly once — and inside the
        // registered single flight, where `join`, `quiesce` and the drain can see it.
        const refreshed = await deps.getIdToken(true);
        if (!refreshed) {
          // Nothing was ever confirmed: this is an anonymous caller — the public site, the
          // login screen — and it has no session to recover.
          if (confirmedToken === null)
            return { kind: 'skipped', token: undefined };
          // But a session WAS confirmed and Firebase no longer has a user for it: signed
          // out in another tab, account switched, storage cleared. The cookie is still in
          // the browser and would still authenticate as the previous account. Fail closed
          // and let `reset()` be the only way a session ends quietly.
          return { error: noIdentityError(), kind: 'failed', token: undefined };
        }
        token = refreshed;
        // Firebase was awaited, and a sign-out or a tenant switch can land inside that.
        // Writing now would mint a cookie behind a logout that is already on the wire.
        if (
          closed ||
          startEpoch !== epoch ||
          contextKey !== deps.getContextKey()
        ) {
          return { kind: 'skipped', token: undefined };
        }
      }

      const result = await deps.renewCookie(token, contextKey);

      // Landed after `reset()` or after a tenant switch: this describes a session — or a
      // tenant — the app has already left, so it may neither confirm a login nor advance a
      // generation others would trust.
      if (startEpoch === epoch && contextKey === deps.getContextKey()) {
        const key = deps.decodeLoginKey(token);
        confirmedToken = token;
        // An unreadable token can never be "the same login": there is no key to match.
        confirmedLogin = key === null ? null : { contextKey, key };
        confirmedSubject = key?.subject ?? UNKNOWN_SUBJECT;
        generation += 1;
        cooldown = null;
        failureReported = false;
        connectivityFaultSince = null;
        // Last inside the block, so a subscriber reading the guard back sees the whole
        // confirmation — the new generation AND the cleared cooldown — not half of it.
        announceConfirmed();
      }
      return { kind: 'wrote', result, token };
    } catch (error) {
      return { error, kind: 'failed', token };
    }
  };

  /** Can the outcome of an operation we joined stand as our own answer? */
  const adopts = (
    joined: Operation<T>,
    outcome: Outcome<T>,
    source: SessionCookieFailureSource,
    explicitToken: string | undefined,
    enteredAtSequence: number,
    afterMutation: boolean,
  ) => {
    // Signing out. The answer describes the session being left, so nobody gets it.
    if (closed) return false;
    // Another tenant's `/auth/me`, or another auth session's, is never ours to consume.
    if (joined.contextKey !== deps.getContextKey()) return false;
    if (joined.epoch !== epoch) return false;
    // A shared failure is an answer for everyone who was waiting on it — retrying there is
    // how one dead session becomes a request storm — but only WITHIN one login. A caller
    // that named its own token knows whose mint this was: inheriting another login's
    // rejection leaves it with no cookie of its own and no attempt to get one, which is the
    // same line the `wrote` branch below already draws.
    if (outcome.kind === 'failed') {
      if (explicitToken === undefined) return true;
      return sameLoginToken(explicitToken, outcome.token);
    }

    if (source === 'renew') {
      if (outcome.kind !== 'wrote') return false;
      // Already on the wire when we entered, so its answer describes a moment a caller
      // that just CHANGED something cannot vouch for — and its response is what becomes
      // `currentUser`. Claims cannot tell the two apart: a forced refresh preserves
      // `auth_time`, and two of them in the same wall-clock second are byte-identical at
      // that. Only such a caller says so; for anyone else the request in flight is the
      // answer, and re-issuing it is pure cost.
      const predatesUs = afterMutation && joined.sequence <= enteredAtSequence;
      if (outcome.token === explicitToken) return !predatesUs;
      if (explicitToken === undefined) return false;
      // The same SIGN-IN, not merely the same account: a re-login, another identity tenant
      // or another user must never consume this write. Within one login the hourly byte
      // change is nothing — the cookie the mint produced is ours too.
      return sameLoginToken(explicitToken, outcome.token) && !predatesUs;
    }

    // A recovery exists to force a write; a skip does not satisfy it.
    return outcome.kind === 'wrote';
  };

  const execute = async (
    source: SessionCookieFailureSource,
    explicitToken?: string,
    afterMutation = false,
    options?: JoinOptions,
  ): Promise<Outcome<T>> => {
    // Read ONCE, before the first join: everything already stamped is a request that was
    // issued before this caller existed. The epoch and context are captured with it, and
    // every await below is judged against these — a caller that entered under one session
    // never gets re-stamped with a newer one.
    const enteredAtSequence = sequence;
    const enteredEpoch = epoch;
    const enteredContextKey = deps.getContextKey();
    /** Has the world moved on under us while we were awaiting something? */
    const obsolete = () =>
      enteredEpoch !== epoch || enteredContextKey !== deps.getContextKey();

    for (;;) {
      // Signing out. Requests are still released — hanging the logout screen helps nobody,
      // and the cookie they carry is about to be deleted anyway — but nothing re-mints one.
      if (closed)
        return { kind: 'skipped', token: confirmedToken ?? undefined };
      if (obsolete()) throw sessionMovedError();

      // Only a RECOVERY waits for the credential transition. An explicit renewal is how
      // the transition confirms its new login, so holding that one would block the very
      // mint the hold is waiting for — and the transition's own recovery, for the write
      // that ends it, passes with `transitionOwner`.
      if (source === 'recover' && heldByTransition(options)) {
        await awaitTransition(options);
        // Released into a world the hold exists to have changed: back to the top, where
        // `closed` and the entry epoch/context are judged again before anything is minted.
        continue;
      }

      if (inFlight) {
        const joined = inFlight;
        // Tell the barrier we are here. Between this mint settling and the decision below
        // there is a gap in which `inFlight` is already null and a follow-on mint of ours
        // has not been registered yet; a `join()` releasing in that gap would act on an
        // outcome that is not the last word.
        const decision = latch();
        undecided.add(decision.opened);
        try {
          const outcome = await joined.promise;
          // Before policy, before reporting, before starting one of our own: an answer
          // that belongs to a session or tenant the app has left is nobody's to act on.
          if (closed || obsolete()) throw sessionMovedError();
          if (
            adopts(
              joined,
              outcome,
              source,
              explicitToken,
              enteredAtSequence,
              afterMutation,
            )
          ) {
            return applyPolicy(source, outcome);
          }
        } finally {
          // Resolved BEFORE the next iteration registers our own mint, but a resolution
          // only schedules the waiter — it never runs it in the middle of this job — so
          // the barrier sees the decision and its consequence as one step.
          undecided.delete(decision.opened);
          decision.open();
        }
        continue;
      }

      const blocking = activeCooldown(explicitToken);
      if (blocking) {
        // Exactly ONE exception per cooldown entry, and only for an explicit renewal: that
        // user action — a login, a post-mutation refetch — is the only way to discover
        // that a transient failure is over. A recovery is not one: it is the automatic
        // answer to a refusal, and letting it probe is how the pacing disappears.
        // Sharing `bypassConsumed` keeps concurrent/repeated actions bounded.
        const mayBypass = !blocking.bypassConsumed && source === 'renew';
        if (!mayBypass) {
          // Same rule as `applyPolicy`, and it has to be here too: the connection can be
          // back before this entry expires, and then the caller lands on this branch —
          // reporting, seconds later, the fault we just chose to stay quiet about.
          if (!silentConnectivityFault(blocking.error))
            report(blocking, source);
          throw blocking.error;
        }
        // Consumed BEFORE awaiting, so concurrent bypass-capable callers share this one.
        blocking.bypassConsumed = true;
      }

      // Registered BEFORE its asynchronous token read, so `join`, `quiesce` and the drain
      // all include the mint from the moment it is decided on rather than from the moment
      // it reaches the wire.
      sequence += 1;
      const operation: Operation<T> = {
        contextKey: enteredContextKey,
        epoch: enteredEpoch,
        promise: perform(explicitToken, enteredContextKey, enteredEpoch),
        sequence,
      };
      inFlight = operation;
      const outcome = await operation.promise.finally(() => {
        if (inFlight === operation) inFlight = null;
      });
      // Joined callers get this check above; the owner needs it just as much. Its
      // `/auth/me` answered for the session and tenant that were current when it started,
      // and handing that back after a sign-out or a tenant switch means the app adopts the
      // previous account's — or the previous tenant's — user as the current one.
      if (closed || obsolete()) throw sessionMovedError();
      return applyPolicy(source, outcome);
    }
  };

  return {
    beginCredentialTransition: async () => {
      const entered = stamp();
      // Synchronous while nothing holds: the reservation below has to happen in the same
      // job as the call, or two concurrent begins would both find the hold free.
      for (;;) {
        const held = transition;
        if (held === null) break;
        await held.done;
      }
      if (
        closed ||
        entered.epoch !== epoch ||
        entered.contextKey !== deps.getContextKey()
      ) {
        throw sessionMovedError();
      }
      const released = latch();
      const current: { done: Promise<void>; end: () => void } = {
        done: released.opened,
        end: () => undefined,
      };
      const end = () => {
        // Only its own handle clears the hold. A stale `end()` — from a transition a
        // logout already settled, or simply called twice — must not release the one
        // running now, and releasing an already-open latch is nothing.
        if (transition === current) transition = null;
        released.open();
      };
      current.end = end;
      // Reserved BEFORE the drain, so a second begin parks instead of overwriting it.
      transition = current;
      try {
        // Drain what is already being written: the transition is about to invalidate the
        // identity behind it, and a mint landing afterwards would write a cookie for the
        // credential that is being replaced.
        const pending = inFlight;
        if (pending) await pending.promise.catch(() => undefined);
        if (
          closed ||
          entered.epoch !== epoch ||
          entered.contextKey !== deps.getContextKey()
        ) {
          throw sessionMovedError();
        }
        return { end };
      } catch (error) {
        // Never leave the hold standing with no handle to end it.
        end();
        throw error;
      }
    },
    getStamp: stamp,
    isCurrent: (candidate, includeGeneration = false) =>
      candidate !== undefined &&
      candidate.epoch === epoch &&
      candidate.contextKey === deps.getContextKey() &&
      (!includeGeneration || candidate.generation === generation),
    isSameLogin: (token) =>
      confirmedLogin !== null &&
      confirmedLogin.contextKey === deps.getContextKey() &&
      sameKey(confirmedLogin.key, deps.decodeLoginKey(token)),
    join: async (options) => {
      let entered = stamp();
      /**
       * Has this request already waited on a mint? Until it has, it is nothing but a
       * request that has not gone out — the transition may move the session under it and
       * release it into the new one. Once it has waited on a mint, it belongs to the
       * session that mint was for, and a later epoch is movement, not a new stamp.
       */
      let joinedAMint = false;
      for (;;) {
        if (heldByTransition(options)) {
          await awaitTransition(options);
          // Released by a logout instead of by the transition: there is no session left to
          // release it into, and a stamp here would send the request out behind the
          // `POST /auth/logout` that is deleting the cookie it would carry.
          if (closed) throw sessionMovedError();
          if (!joinedAMint) entered = stamp();
        }
        if (
          entered.epoch !== epoch ||
          entered.contextKey !== deps.getContextKey()
        ) {
          throw sessionMovedError();
        }
        const pending = inFlight;
        // Nothing is being written, so there is nothing to wait for. The barrier is a
        // synchronizer, not a freshness check: it cannot read an HttpOnly cookie, and a
        // request that turns out to carry a dead one converges through `recover`.
        if (!pending) {
          if (heldByTransition(options)) continue;
          return stamp();
        }
        joinedAMint = true;
        const outcome = await pending.promise;
        // The mint is over and nothing has replaced it — but a caller parked on it may be
        // one microtask away from starting the follow-on that will. Let those decisions
        // land first: a re-login queued behind a failed mint is about to write a cookie
        // this request can go out on, and the failure is not this request's answer.
        if (inFlight === null && undecided.size > 0) {
          await Promise.all([...undecided]);
        }
        if (
          entered.epoch !== epoch ||
          entered.contextKey !== deps.getContextKey()
        ) {
          throw sessionMovedError();
        }
        // The mint itself may have belonged to a session that has since been left.
        if (
          pending.epoch !== epoch ||
          pending.contextKey !== deps.getContextKey()
        ) {
          throw sessionMovedError();
        }
        // A follow-on mint started behind that one; releasing now would send the request
        // out on top of a cookie that is halfway rewritten.
        if (inFlight !== null && inFlight !== pending) continue;
        // `Operation.promise` resolves to an Outcome, failures included. Treating a
        // `failed` one as success releases the request on a cookie nobody ever wrote.
        if (outcome.kind === 'failed') throw outcome.error;
        // The hold was judged when this request ENTERED, and it has been awaiting a mint
        // ever since — the very mint a transition beginning behind it drains. Releasing on
        // that drain would put the one request the hold exists to stop on the wire, with
        // the credential that is being replaced. Back to the top, where it parks.
        if (heldByTransition(options)) continue;
        return stamp();
      }
    },
    onConfirmed: (listener) => {
      confirmationListeners.add(listener);
      return () => {
        confirmationListeners.delete(listener);
      };
    },
    recover: async (rejected, options) => {
      // Held before the stamp is judged at all: it describes the session as it was BEFORE
      // the credential was rewritten, and judging it against an identity that is halfway
      // replaced answers the wrong question. After the hold the verdict is the true one —
      // usually `'stale-session'`, because the transition moved the epoch.
      await awaitTransition(options);
      if (rejected) {
        // The request was issued under an auth session or an acting tenant that is no
        // longer current. Renewing would not make it replayable — replaying it is exactly
        // what must not happen — so say so instead of resolving like a success.
        if (
          rejected.epoch !== epoch ||
          rejected.contextKey !== deps.getContextKey()
        ) {
          return 'stale-session';
        }
        // Someone already minted for this epoch and context: the cookie in the browser is
        // newer than the one this request was rejected for, so a replay is enough.
        if (rejected.generation !== generation) return 'already-current';
      }
      const outcome = await execute('recover', undefined, false, options);
      // Nothing was written. The only way a recovery gets here is the anonymous branch —
      // no Firebase identity AND nothing ever confirmed — and saying 'renewed' would send
      // the caller off to replay the request with the credential it was just refused for.
      if (outcome.kind !== 'wrote') return 'stale-session';
      return 'renewed';
    },
    quiesce: async () => {
      closed = true;
      // Closed FIRST, then the hold is settled: a logout in the middle of a credential
      // transition leaves an owner that will never call `end()`, and everything parked on
      // it would wait for a release that is not coming. They wake into the `closed` branch,
      // which is the honest answer — the session they were waiting for is being left.
      transition?.end();
      // Read AFTER closing, so nothing can slip in between. At most one operation can be
      // running by then, and no further one can start.
      const pending = inFlight;
      if (pending) await pending.promise.catch(() => undefined);
    },
    renew: async (token, options) => {
      const outcome = await execute(
        'renew',
        token,
        options?.afterMutation ?? false,
      );
      if (outcome.kind === 'wrote') return outcome.result;
      // The session is being left; an answer describing it is exactly what must not be
      // handed back. Same discriminant the mid-renewal sign-out already throws, so
      // `AuthProvider`'s listener chain stays silent about it instead of reporting it.
      if (closed) throw sessionMovedError();
      // Unreachable: an explicit renewal either writes, adopts a write, or throws.
      throw new Error('Session cookie renewal returned no response');
    },
    replayRefused: (stamp, error) => {
      // Not about the cookie in the browser now: never through the barrier, a previous
      // auth session, another acting tenant, or a write that has since been superseded.
      // Each of those is handled — or deliberately ignored — by the paths that own it, and
      // the Axios layer checks `isCurrent(stamp, true)` before it exposes an auth failure
      // at all, so a stale replay's error never reaches the cache subscribers either.
      if (
        stamp === undefined ||
        stamp.epoch !== epoch ||
        stamp.contextKey !== deps.getContextKey() ||
        stamp.generation !== generation
      ) {
        return;
      }
      // The server refused the cookie the latest write produced, so that write is not
      // evidence of anything: not of a live cookie, and not of a confirmed login the
      // listener may skip a mint for. The account it belonged to survives — the cooldown
      // is keyed by it — and that cooldown is what keeps the next request from minting
      // again: one `/auth/me` per cooldown instead of one per query, each followed by the
      // same refusal. An explicit login keeps its one probe, as it does for every entry.
      const entry = rememberFailure({
        error,
        token: confirmedToken ?? undefined,
      });
      confirmedToken = null;
      confirmedLogin = null;
      report(entry, 'replay');
    },
    reset: () => {
      // The credential transition is deliberately NOT ended here. `reset()` is what its own
      // new sign-in calls, and the hold has to outlive that: the confirming `/auth/me` has
      // not been issued yet, and releasing everyone onto the new epoch before it lands is
      // exactly the race the hold exists to prevent. Only `end()` — or a logout — settles it.
      closed = false;
      confirmedToken = null;
      confirmedLogin = null;
      confirmedSubject = null;
      cooldown = null;
      failureReported = false;
      connectivityFaultSince = null;
      epoch += 1;
    },
  };
}
